import React, { useEffect, useState, useCallback } from 'react';
import './styles.css';
import HomePage from './components/HomePage';
import AddNote from './components/AddNote';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import NoteDetails from './components/NoteDetails';
import AddExpense from './components/AddExpense';
import EditExpense from './components/EditExpense';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToHome = useCallback(() => {
    if (!isTransitioning) {
      navigate('/');
    }
  }, [navigate, isTransitioning]);
  
  const handleLogin = async (email, password) => {
    try {
      const response = await fetch('https://iti-app-nf.dev.capdigit-solutions.fr/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
        credentials: 'include'
      });

      const data = await response.json();
      
      if (!response.ok) {
        setError(data.message || 'Erreur lors de la connexion');
        return;
      }
      
      localStorage.setItem('employeeId', data.employeeId);
      
      setIsLoggedIn(true);
      setError('');
      navigate('/');
    } catch (err) {
      console.error('Erreur', err);
      setError('Erreur de connexion au serveur');
    }
  };

  const handleLogout = useCallback(async () => {
    try {
      await fetch('https://iti-app-nf.dev.capdigit-solutions.fr/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });
      localStorage.removeItem('employeeId');
      setIsLoggedIn(false);
      navigate('/');
    } catch (err) {
      console.error('Erreur lors de la déconnexion:', err);
    }
  }, [navigate]);

  useEffect(() => {
    let isMounted = true;

    const restoreSession = async () => {
      try {
        const response = await fetch('/api/employee/profile', {
          method: 'GET',
          credentials: 'include'
        });

        if (!isMounted) {
          return;
        }

        if (response.ok) {
          const profile = await response.json();
          if (profile.employeeId) {
            localStorage.setItem('employeeId', profile.employeeId);
          }
          setIsLoggedIn(true);
        } else {
          localStorage.removeItem('employeeId');
          setIsLoggedIn(false);
        }
      } catch (error) {
        console.error('Erreur lors de la restauration de session:', error);
        if (isMounted) {
          setIsLoggedIn(false);
        }
      } finally {
        if (isMounted) {
          setIsAuthLoading(false);
        }
      }
    };

    restoreSession();

    return () => {
      isMounted = false;
    };
  }, []);

  if (isAuthLoading) {
    return (
      <div className="page-container">
        <div className="login-page">
          <p>Connexion en cours...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {isLoggedIn ? (
        <TransitionGroup>
          <CSSTransition
            key={location.pathname}
            timeout={{
              enter: 300,
              exit: 150
            }}
            classNames="page-transition"
            onEnter={() => {
              setIsTransitioning(true);
            }}
            onEntered={() => {
              setIsTransitioning(false);
            }}
          >
            <div className="page-wrapper">
              <Routes>
                <Route path="/" element={<HomePage handleLogout={handleLogout} goToHome={goToHome} />} />
                <Route path="/notes-list" element={<HomePage handleLogout={handleLogout} goToHome={goToHome} />} />
                <Route path="/add-note" element={<AddNote handleLogout={handleLogout} goToHome={goToHome} />} />
                <Route path="/get-expenses/:noteId" element={<NoteDetails handleLogout={handleLogout} goToHome={goToHome} />} />
                <Route path="/add-expense/:noteId/:expenseType" element={<AddExpense handleLogout={handleLogout} goToHome={goToHome} />} />
                <Route path="/edit-expense/:noteId/:expenseId" element={<EditExpense handleLogout={handleLogout} goToHome={goToHome} />} />
              </Routes>
            </div>
          </CSSTransition>
        </TransitionGroup>
      ) : (
        <div className="login-page">
          <img src='/iTi.png' alt='iTi logo' width={125} height={108}/>
          <form onSubmit={(e) => {
            e.preventDefault();
            const email = e.target.elements.email?.value;
            const password = e.target.elements.password?.value;

            if (!email || !password) {
              setError("Email et mot de passe sont requis");
              return;
            }

            handleLogin(email, password);
          }}>
            <div className="input-container-connexion">
              <svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className='icon'>
                <text x="50%" y="50%" text-anchor="middle" fill="#94a3b8" dy=".3em" font-size="100">@</text>
              </svg>
              <input required type="text" name="email" autoComplete="on" className="input" placeholder="Email" />
            </div>
              
            <div className="input-container-connexion">
              <svg stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="icon">
                <path d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" strokeLinejoin="round" strokeLinecap="round"></path>
              </svg>
              <input required className="input" type="password" name="password" placeholder="Mot de passe" />
            </div>
            <button className='button-connect' type="submit">Se connecter</button>
            {error && <div className="error-message"><span className="error-icon">⚠️</span>{error}</div>}
          </form>
        </div>
      )}
    </div>
  );
}

export default App;
